import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Modal from 'components/Modal';
import Table, { TableProps } from 'components/Table';
import Spinner from 'components/Spinner/Spinner';
import actions from 'state/actions';
import selectors from 'state/selectors';
import services from 'services';
import utils from 'utils';
import styled from 'styled-components/macro';
import { parseCsvToTableConfig } from 'utils/helpers/csv.helper';
import { ReportFilters } from 'models/request';

const TableWrapper = styled.div`
  padding: 0 16px 16px;
`;

const EmptyMessage = styled.div`
  padding: 24px 16px;
  color: ${({ theme }) => theme.colorPalette.grey.grey7};
  text-align: center;
`;

const ViewKpiReport = () => {
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();

  const userPreference = useSelector(selectors.user.getUserPreference);

  const modalProps: { filters: ReportFilters } = useSelector(selectors.modal.getModalProps);
  const { filters } = modalProps || {};

  const [tableConfig, setTableConfig] = useState<ReturnType<typeof parseCsvToTableConfig> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!filters) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await services.execKpiReport(filters);
        // atob may produce a JSON-encoded string (with surrounding quotes) — strip them
        const raw = atob(data.file);
        const csvText = raw.startsWith('"') ? JSON.parse(raw) : raw;
        setTableConfig(parseCsvToTableConfig(csvText));
      } catch (e) {
        console.error(e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters]);

  const handleClose = () => dispatch(actions.modal.closeModal());

  const columns = tableConfig?.columnsList ?? [];
  const rows = tableConfig?.rows ?? [];

  const dataColumns: TableProps['columns'] = [...columns].map((column) => {
    const prepared = utils.table.prepareColumns(
      column,
      userPreference.dateFormat || 'dd/MM/yyyy',
      rows,
      userPreference.timeZone,
    );
    if (column.type === 'Date') {
      return {
        ...prepared,
        Cell: ({ cell }: { cell: any }) => {
          const formatted = utils.date.convertDate(cell.value, userPreference.timeZone);
          const dateOnly = typeof formatted === 'string' ? formatted.split(',')[0] : formatted;
          return <div title={dateOnly}>{dateOnly}</div>;
        },
      };
    }
    return prepared;
  });

  return (
    <>
      <Modal.Header title={t('kpi-report-view-title')} />
      <TableWrapper>
        {loading && <Spinner size={40} />}
        {!loading && (error || (tableConfig && rows.length === 0)) && (
          <EmptyMessage>{t('no-data-available')}</EmptyMessage>
        )}
        {!loading && !error && tableConfig && rows.length > 0 && (
          <Table id="kpi-report-view-table" columns={dataColumns} rows={rows} hasSort hasFilter hasPagination />
        )}
      </TableWrapper>
      <Modal.Footer withConfirmButton={false} cancelAction={handleClose} closeButtonText={t('close')} />
    </>
  );
};

export default ViewKpiReport;
